import useUnleashFeatureFlag from './useUnleashFeatureFlag';
import { isGisParams } from '@scale/shared/assignmentJson/isGisAssignment';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { isImageAnnotation } from '@scale/shared/assignmentJson/types/ImageAnnotationAssignmentJson';
import { LidarTopdownParams } from '@scale/shared/types/params/lidar';
import { isNamedEntityRecognition } from '@scale/shared/assignmentJson/types/NamedEntityRecognitionAssignmentJson';
import { VideoPlaybackParams } from '@scale/shared/types/params/videoplayback';
import _ from 'lodash';
import { ContextAttachmentType } from '@scale/shared/types/params/annotation';

/**
 * Returns feature flags common to queue, audit, scenario, corp preview.
 * NOTE: Changes here will impact all environments including audit, training, corp, and remotasks!
 * @param projectId Project id
 * @param subtaskId Subtask id
 * @param params Task params
 */
// eslint-disable-next-line complexity
export const useCommonFeatureFlags = (assignment?: AssignmentJson) => {
  const { projectId, params, type } = assignment ?? {};

  const shouldUseVideoAnnotationLiteMode = useUnleashFeatureFlag(
    '2d-videoannotation-lite-mode',
    { projectId },
    false,
  );

  const shouldUseVideoAnnotationLiteModeSettings = useUnleashFeatureFlag(
    '2d-videoannotation-lite-mode-settings',
    { projectId },
    false,
  );

  const shouldUse2DSemsegUXRevamp = useUnleashFeatureFlag(
    '2d-semseg-ux-revamp',
    { projectId },
    false,
  );

  const sidebarOnlyRenderVisible = useUnleashFeatureFlag(
    'sidebar-only-render-visible',
    { projectId },
    false,
  );

  const linkedAnnotationGrouping = useUnleashFeatureFlag(
    'linked-annotation-grouping',
    { projectId },
    false,
  );

  const unifiedSidebarNoDebounce = useUnleashFeatureFlag(
    'unified-sidebar-no-debounce',
    { projectId },
    false,
  );

  const attributeSearchSelect = useUnleashFeatureFlag(
    'attribute-search-select',
    { projectId },
    false,
  );

  const disableAutosave = useUnleashFeatureFlag('disable-autosave', { projectId }, false);

  const allowForceMerge = useUnleashFeatureFlag('2d-allow-force-merge', { projectId }, false);

  const allowAnnotationMultipleFrameUpdate = useUnleashFeatureFlag(
    '2d-allow-annotation-multiple-frame-update',
    { projectId },
    false,
  );

  const gisCommonFeatures =
    useUnleashFeatureFlag('gis-common-features', { projectId }, false) || isGisParams(params);

  // TODO: Enable with gisCommonFeatures once the new link list is tested
  const useNewLinkList = !!localStorage.useNewLinkList;

  const canUseCameraArrows =
    useUnleashFeatureFlag('camera-arrows', { projectId }, false) ||
    gisCommonFeatures ||
    !!localStorage.cameraArrows;

  const canEditGroundMesh =
    gisCommonFeatures && !!(params as LidarTopdownParams)?.can_edit_ground_mesh;

  const hasPhotocolorContext = !!_.find(
    (assignment?.params as LidarTopdownParams)?.context_attachments ?? [],
    a => a.type === ContextAttachmentType.GIS_PHOTOCOLOR_PNG_V1,
  );

  const canGenerateDrivableRegionZones =
    (!!useUnleashFeatureFlag('drivable-region-zones', { projectId }, false) ||
      localStorage.canGenerateDrivableRegionZones) &&
    !!assignment;

  const canGenerateDrivableRegions =
    (!!useUnleashFeatureFlag('drivable-regions', { projectId }, false) ||
      localStorage.canGenerateDrivableRegions) &&
    !!assignment;

  const logVerboseErrors = localStorage.logVerboseErrors;

  const shouldAllowExtendedSemsegLabels = useUnleashFeatureFlag(
    'allow-extended-semseg-labels',
    { projectId },
    false,
  );

  const shouldSkipLTDValidation = useUnleashFeatureFlag(
    'should-skip-ar-validation',
    { projectId },
    false,
  );

  const canAccessNewLinterPanel =
    useUnleashFeatureFlag('new-linter-panel', { projectId }, false) ||
    gisCommonFeatures ||
    !!localStorage.experimentalLinterAccess;

  // Using this flag to roll out new lint panel to all imageannotation tasks with a kill switch
  const useNewLintPanelImageAnnotation =
    !!useUnleashFeatureFlag('imageannotation-new-lint-panel', { projectId }, false) &&
    !!assignment &&
    !!isImageAnnotation(assignment);

  const showOldAnnotationSidebar =
    useUnleashFeatureFlag('use-old-annotation-sidebar', { projectId }, false) ||
    !!localStorage.showOldAnnotationSidebar;

  const useLintAutofix =
    useUnleashFeatureFlag('use-lint-autofix', { projectId }, false) ||
    !!localStorage.useLintAutofix;

  const limitPrefetchedImages = useUnleashFeatureFlag(
    'limit-prefetched-images',
    { projectId },
    false,
  );

  const limitPrefetchedLargeImages = useUnleashFeatureFlag(
    'limit-prefetched-large-images',
    { projectId },
    false,
  );

  const videoannotationAttributePlaybackBars = useUnleashFeatureFlag(
    'videoannotation-attribute-playback-bars',
    { projectId },
    false,
  );

  const prerenderFrames = useUnleashFeatureFlag('pre-render-frames', { projectId }, false);

  const disableTaskerPrefetchLimit = useUnleashFeatureFlag(
    'disable-tasker-prefetch-limit',
    { projectId },
    false,
  );

  const downloadVideo =
    useUnleashFeatureFlag('download-video', { projectId }, false) || !!localStorage.downloadVideo;

  const enableAutoAnnotatePolygonTool =
    useUnleashFeatureFlag('enable-auto-segment-polygon-tool', { projectId }, false) ||
    (!!assignment?.isScaleStudio && isImageAnnotation(assignment)) ||
    !!localStorage.enableAutoAnnotatePolygonTool;

  const enableAutoAnnotateInBrowser =
    useUnleashFeatureFlag('enable-autoannotate-in-browser', { projectId }, false) ||
    !!localStorage.enableAutoAnnotateInBrowser;

  const enableAutoSegmentLineTool =
    useUnleashFeatureFlag('enable-auto-segment-line-tool', { projectId }, false) ||
    !!localStorage.enableAutoSegmentLineTool;

  const enableAutoAnnotateBoxTool =
    useUnleashFeatureFlag('enable-auto-annotate-box-tool', { projectId }, false) ||
    !!localStorage.enableAutoAnnotateBoxTool;

  const useAutoflagSidebarTools =
    useUnleashFeatureFlag('use-autoflag-sidebar-tools', { projectId }, false) ||
    !!localStorage.useAutoflagSidebarTools;

  const useUnifiedSidebar = useUnleashFeatureFlag('2d-sidebar-unification', { projectId }, false);

  const enableCollapsibleSidebar = useUnleashFeatureFlag(
    '2d-sidebar-collapse',
    { projectId },
    false,
  );

  const useThinLinkLines = useUnleashFeatureFlag('use-thin-link-lines', { projectId }, false);

  const enableDragToRevealPrelabelsTool = !!params?.active_prelabels;

  const enableSavingPinsInUrl =
    useUnleashFeatureFlag('enable-saving-pins-in-url', { projectId }, false) && assignment?.isQueue;

  // Enabled rotating images with mouse while holding shift
  const enable2dMouseRotation = useUnleashFeatureFlag(
    'enable-2d-mouse-rotation',
    { projectId },
    false,
  );

  const imageRenderingAuto =
    useUnleashFeatureFlag('image-rendering-auto', { projectId }, false) ||
    !!localStorage.imageRenderingAuto;

  // Image alignment, allowing context images to be aligned and overlaid on top of main image
  const alignmentSettingEnabled = useUnleashFeatureFlag(
    'context-attachment-alignment',
    { projectId: assignment?.projectId },
    false,
  );

  // draggable and resizable camera context window popup
  const useWindowCameraContext =
    useUnleashFeatureFlag(
      'use-window-camera-context',
      { projectId: assignment?.projectId },
      false,
    ) || !!localStorage.useWindowCameraContext;

  // gis demo: overlay osm to determine what to label
  const gisShowOsmImage = useUnleashFeatureFlag('gis-show-osm-image', { projectId }, false);
  const enableLidarTopdownZEditor = useUnleashFeatureFlag(
    'enable-lidartopdown-zeditor',
    { projectId },
    false,
  );

  const forceImageAnnotationLiteUI = useUnleashFeatureFlag(
    'force-imageannotation-lite-ui',
    { projectId },
    false,
  );

  const virtualizeImageAnnotationList = useUnleashFeatureFlag(
    'virtualize-imageannotation-list',
    { projectId },
    false,
  );

  const visualizeGroundMetricsImages = useUnleashFeatureFlag(
    'gis-visualize-ground-metrics',
    { projectId },
    false,
  );

  const activateAudioWaveformFlag =
    useUnleashFeatureFlag('2d-vpb-waveform', { projectId }, false) ?? false;

  const allowOutOfViewportVertices =
    useUnleashFeatureFlag('allow-out-of-viewport-vertices', { projectId }, false) ?? false;

  const enableKeyframes =
    useUnleashFeatureFlag('2d-enable-keyframes', { projectId }, false) ||
    !!localStorage.experimentalEnableKeyframes;

  const enableKeyframesJumping = useUnleashFeatureFlag(
    '2d-enable-keyframes-jumping',
    { projectId },
    false,
  );

  const useAutoSmartTracking = useUnleashFeatureFlag('auto-smart-tracking', { projectId }, false);

  const useSmartTracking =
    // What's broken here is that taskType is not needed, projectId will implicitly bring up taskType in the backend.
    // Will not remove just in case I haven't tested thoroughly, but you should fix the as any (aka $FIXME) assertion
    useUnleashFeatureFlag(
      'enable-ml-interpolation',
      { projectId, taskType: type } as $FIXME,
      false,
    ) ||
    !!localStorage.useSmartTracking ||
    useAutoSmartTracking ||
    !!(params as VideoPlaybackParams)?.tracking_prelabels?.annotations;

  const disableInterpolation = useUnleashFeatureFlag('disable-interpolation', { projectId }, false);

  const canAccessLidarLinkingMinimap = useUnleashFeatureFlag(
    'lidarlinking-minimap',
    { projectId },
    false,
  );

  const enableRescaledZoom = useUnleashFeatureFlag(
    '2d-imageannotation-rescaled-zoom',
    { projectId },
    false,
  );

  // New label selector that uses sequential selection instead of showing the entire choice tree
  const useNewLabelSelector = !!useUnleashFeatureFlag(
    'new-label-selector',
    { projectId: assignment?.projectId },
    false,
  );

  // Hack for Prime Air - emphasize first point in polygon to make it stand out
  const emphasizeFirstPolygonPoint = !!useUnleashFeatureFlag(
    '2d-emphasize-first-point',
    { projectId: assignment?.projectId },
    false,
  );

  const canUseGrammarCheck = useUnleashFeatureFlag(
    'vpb-grammar-check-annotation-attributes',
    { projectId },
    false,
  );

  const colorPolygonPoints = useUnleashFeatureFlag(
    'color-polygon-points',
    { projectId: assignment?.projectId },
    false,
  )
    ? ['blue', 'green', 'yellow', 'red', 'orange', 'indigo']
    : undefined;

  const eventAttributesOptionalByDefault = useUnleashFeatureFlag(
    'event-attributes-optional-by-default',
    { projectId: assignment?.projectId },
    false,
  );

  const rightAlignText = useUnleashFeatureFlag(
    'right-align-text',
    { projectId: assignment?.projectId },
    false,
    assignment && isNamedEntityRecognition(assignment),
  );

  const skipAnnotationAttributeChoiceValidation = useUnleashFeatureFlag(
    'skip-annotation-attribute-choice-validation',
    { projectId: assignment?.projectId },
    false,
  );

  const categoryFieldSectionWrapper = useUnleashFeatureFlag(
    'category-field-section-wrapper',
    { projectId: assignment?.projectId },
    false,
  );

  const enableAttributeInputInLabelSelector = useUnleashFeatureFlag(
    'enable-attribute-input-in-label-selector',
    { projectId: assignment?.projectId },
    false,
  );

  const colorByAnnotationAttribute = useUnleashFeatureFlag(
    'color-by-annotation-attribute',
    { projectId: assignment?.projectId },
    false,
  );

  const delimitedAttributeLabels = useUnleashFeatureFlag(
    'delimited-attribute-labels',
    { projectId: assignment?.projectId },
    false,
  );

  const useAFMSuperpixel = useUnleashFeatureFlag(
    'afm-superpixel',
    { projectId: assignment?.projectId },
    false,
  );

  const enableGeocontextSidebar = useUnleashFeatureFlag(
    'enable-geocontext-sidebar',
    { projectId: assignment?.projectId },
    false,
  );

  return {
    gisCommonFeatures,
    useNewLinkList,
    hasPhotocolorContext,
    canGenerateDrivableRegionZones,
    canGenerateDrivableRegions,
    logVerboseErrors,
    canUseCameraArrows,
    canAccessNewLinterPanel: canAccessNewLinterPanel || useNewLintPanelImageAnnotation,
    shouldSkipLTDValidation,
    showOldAnnotationSidebar,
    useLintAutofix,
    limitPrefetchedImages,
    limitPrefetchedLargeImages,
    prerenderFrames,
    disableTaskerPrefetchLimit,
    videoannotationAttributePlaybackBars,
    downloadVideo,
    enableAutoAnnotatePolygonTool,
    enableAutoAnnotateInBrowser,
    enableAutoAnnotateBoxTool,
    enableAutoSegmentLineTool,
    enableDragToRevealPrelabelsTool,
    enableSavingPinsInUrl,
    enable2dMouseRotation,
    useAutoflagSidebarTools,
    useUnifiedSidebar,
    enableCollapsibleSidebar,
    useThinLinkLines,
    imageRenderingAuto,
    alignmentSettingEnabled,
    useWindowCameraContext,
    visualizeGroundMetricsImages,
    gisShowOsmImage,
    enableLidarTopdownZEditor,
    forceImageAnnotationLiteUI,
    virtualizeImageAnnotationList,
    canEditGroundMesh,
    activateAudioWaveformFlag,
    allowOutOfViewportVertices,
    enableKeyframes,
    enableKeyframesJumping,
    useSmartTracking,
    disableInterpolation,
    canAccessLidarLinkingMinimap,
    enableRescaledZoom,
    shouldUse2DSemsegUXRevamp,
    sidebarOnlyRenderVisible,
    disableAutosave,
    useNewLabelSelector,
    emphasizeFirstPolygonPoint,
    shouldAllowExtendedSemsegLabels,
    canUseGrammarCheck,
    shouldUseVideoAnnotationLiteMode,
    shouldUseVideoAnnotationLiteModeSettings,
    allowForceMerge,
    allowAnnotationMultipleFrameUpdate,
    useAutoSmartTracking,
    colorPolygonPoints,
    linkedAnnotationGrouping,
    unifiedSidebarNoDebounce,
    attributeSearchSelect,
    eventAttributesOptionalByDefault,
    enableAttributeInputInLabelSelector,
    rightAlignText,
    skipAnnotationAttributeChoiceValidation,
    categoryFieldSectionWrapper,
    colorByAnnotationAttribute,
    delimitedAttributeLabels,
    useAFMSuperpixel,
    enableGeocontextSidebar,
  };
};
